import React, { useState } from 'react';
import { DrawerHeader } from '@components/drawer-header';
import {
    Card,
    CardContent,
    Typography,
    Stack,
    CircularProgress,
    Box,
    Grid,
    Paper,
    useTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BugReportIcon from '@mui/icons-material/BugReport';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDebugStatus, triggerCoredumpUpload, listCoredumps, analyzeCoredump, deleteCoredump, DebugMachineStatus, CoredumpMetadata } from './api';

export function DebugFirmwarePage() {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const [selectedMachine, setSelectedMachine] = useState<DebugMachineStatus | null>(null);
    const [analysisOutput, setAnalysisOutput] = useState<string | null>(null);
    const [isFullScreen, setIsFullScreen] = useState(false);

    const { data: machines = [], isLoading } = useQuery({
        queryKey: ['debug-firmware-status'],
        queryFn: getDebugStatus,
    });

    const triggerUploadMutation = useMutation({
        mutationFn: (params: { id: string, tid: string }) => triggerCoredumpUpload(params.id, params.tid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['debug-firmware-status'] });
            alert('Comando de upload enviado com sucesso!');
        },
        onError: () => {
            alert('Erro ao enviar comando de upload.');
        }
    });

    const { data: coredumps = [], isLoading: loadingCoredumps } = useQuery({
        queryKey: ['coredumps', selectedMachine?.tid],
        queryFn: () => listCoredumps(selectedMachine!.tid),
        enabled: !!selectedMachine,
    });

    const analyzeMutation = useMutation({
        mutationFn: analyzeCoredump,
        onSuccess: (data) => {
            setAnalysisOutput(data);
        },
        onError: () => {
            setAnalysisOutput('Falha ao analisar o coredump.');
        }
    });

    const deleteCoredumpMutation = useMutation({
        mutationFn: deleteCoredump,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['coredumps', selectedMachine?.tid] });
        }
    });

    return (
        <DrawerHeader title="Debug Firmware">
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            bgcolor: 'background.paper',
                            borderLeft: `6px solid ${theme.palette.error.main}`,
                            borderRadius: 2,
                            mb: 1
                        }}
                    >
                        <Box>
                            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 600 }}>
                                AUTOMNI ADMIN
                            </Typography>
                            <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Análise de Coredump</Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                Monitore anomalias de boot, solicite upload de coredumps dos dispositivos e realize análises automáticas usando o ESP-IDF.
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Card>
                <CardContent>
                    {isLoading ? (
                        <Stack alignItems="center" mt={4}>
                            <CircularProgress />
                        </Stack>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Equipamento</TableCell>
                                        <TableCell>TID</TableCell>
                                        <TableCell>Modelo</TableCell>
                                        <TableCell>Status (Anomalia)</TableCell>
                                        <TableCell align="right">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {machines.map((m) => (
                                        <TableRow key={m.id}>
                                            <TableCell>{m.name}</TableCell>
                                            <TableCell>{m.tid}</TableCell>
                                            <TableCell>{m.modelo}</TableCell>
                                            <TableCell>
                                                {m.has_boot_anomaly ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                                                        <WarningAmberIcon fontSize="small" sx={{ mr: 1 }} />
                                                        <Typography variant="body2" fontWeight="bold">Anomalia de Boot Detectada</Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">Normal</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    {m.has_boot_anomaly && (
                                                        <Tooltip title={m.is_upload_pending ? "Comando enviado, aguardando equipamento..." : "Solicitar Upload de Coredump via MQTT/Socket"}>
                                                            <span>
                                                                <Button 
                                                                    variant="outlined" 
                                                                    color={m.is_upload_pending ? "warning" : "error"} 
                                                                    size="small"
                                                                    startIcon={<DownloadIcon />}
                                                                    disabled={triggerUploadMutation.isPending || m.is_upload_pending}
                                                                    onClick={() => triggerUploadMutation.mutate({ id: m.id, tid: m.tid })}
                                                                >
                                                                    {m.is_upload_pending ? "Aguardando" : "Upload"}
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                    {m.coredump_count > 0 && (
                                                        <Button 
                                                            variant="contained" 
                                                            color="primary" 
                                                            size="small"
                                                            startIcon={<BugReportIcon />}
                                                            onClick={() => setSelectedMachine(m)}
                                                        >
                                                            Analisar
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Coredump List Modal */}
            <Dialog 
                open={!!selectedMachine} 
                onClose={() => { setSelectedMachine(null); setAnalysisOutput(null); setIsFullScreen(false); }}
                maxWidth="md"
                fullWidth
                fullScreen={isFullScreen}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Coredumps: {selectedMachine?.name} ({selectedMachine?.tid})</Typography>
                    <IconButton onClick={() => { setSelectedMachine(null); setAnalysisOutput(null); setIsFullScreen(false); }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {loadingCoredumps ? (
                        <CircularProgress />
                    ) : (
                        <Stack spacing={2}>
                            {coredumps.length === 0 ? (
                                <Typography>Nenhum coredump baixado para este dispositivo.</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Arquivo</TableCell>
                                            <TableCell align="right">Ação</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {coredumps.map((dump: CoredumpMetadata) => (
                                            <TableRow key={dump.filename}>
                                                <TableCell>
                                                    {dump.filename}
                                                    {!dump.has_elf && (
                                                        <Typography variant="caption" color="error" display="block">
                                                            Sem arquivo ELF correspondente ({dump.elf_file || 'N/A'})
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                                        {dump.has_elf ? (
                                                            <Button 
                                                                variant="outlined" 
                                                                color="primary"
                                                                size="small"
                                                                disabled={analyzeMutation.isPending}
                                                                onClick={() => analyzeMutation.mutate(dump.filename)}
                                                            >
                                                                Extrair Log
                                                            </Button>
                                                        ) : (
                                                            <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                                                                Análise indisponível
                                                            </Typography>
                                                        )}
                                                        <Tooltip title="Excluir Coredump">
                                                            <IconButton 
                                                                color="error" 
                                                                size="small"
                                                                onClick={() => {
                                                                    if (window.confirm('Tem certeza que deseja excluir este coredump?')) {
                                                                        deleteCoredumpMutation.mutate(dump.filename);
                                                                    }
                                                                }}
                                                                disabled={deleteCoredumpMutation.isPending}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}

                            {/* Analysis Output Section */}
                            {analyzeMutation.isPending && (
                                <Box mt={2}>
                                    <Typography variant="body2" mb={1}>Processando dump com ESP-IDF...</Typography>
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                            
                            {analysisOutput && (
                                <Box mt={3} sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, height: isFullScreen ? '100%' : 'auto' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle2">Resultado da Análise:</Typography>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="Copiar">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => navigator.clipboard.writeText(analysisOutput)}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={isFullScreen ? "Sair da Tela Cheia" : "Tela Cheia"}>
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => setIsFullScreen(!isFullScreen)}
                                                >
                                                    {isFullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            p: 2, 
                                            bgcolor: '#1e1e1e', 
                                            color: '#d4d4d4',
                                            fontFamily: 'monospace',
                                            maxHeight: isFullScreen ? 'none' : '400px',
                                            flexGrow: isFullScreen ? 1 : 0,
                                            overflowY: 'auto',
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        {analysisOutput}
                                    </Paper>
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setSelectedMachine(null); setAnalysisOutput(null); }}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </DrawerHeader>
    );
}
